import {
    current,
    next,
    account,
    meta,
    autoBuy as autoBuyState,
    resetAutoBuy,
    initialize,
    isFreeBuyConfirm,
    getTicketCost,
    getMaxCardsCanBuy,
    getBalance,
    openModal,
    closeModal,
    pickedCards,
    screenConfig as screenConfigState,
} from '../../stores/backend';
import { BackendSocket } from '../../hooks/use-socket';
import { getQuery } from '../../lib/query';
import { BingoLoadGameResponse } from '../../types/bingo-events/in/backend/load-game-response';
import { InjectedStateInfo } from '../../types/bingo-events/out/backend/injected-state-info';
import { isSuspended, onSuspend, suspendApp } from '../../lib/suspension';
import {
    beginCurrentLoad,
    cancelCurrentLoad,
    finishCurrentLoad,
} from './session';

export type BackendPayload = ReturnType<typeof createBackendPayload>;

const LOAD_GAME_WARNING_DELAY = 30000;
const LOAD_GAME_TIMEOUT = 120000;
const SLOW_RESPONSE_WARNING = 'warning.slowResponse';

export const createBackendPayload = (socket: BackendSocket) => {
    let disposed = false;
    const active = () => !disposed && !isSuspended() && socket.connected;
    const cancellations = new Set<() => void>();
    const { USERID: userId, SESSIONID: sessionId } = getQuery();
    const { screenConfig } = screenConfigState.value;
    const game_id = screenConfig.room.gameId;

    const injectStateInfo = <T>(data: T): T & InjectedStateInfo => ({
        ...data,
        _stateInfo: {
            userId,
            sessionId,
            gameId: game_id,
        },
    });

    /**
     * If `param` is a number then it is treated as a `bingo:buyCards` output event,
     * otherwise, it will be treated as a `bingo:buyPickedCards` output event
     * @param param
     */
    const buyTicket = (param: number | string[], useTokensFirst = false) => {
        if (!active()) return;
        const { game_sid: sid, game_number } = next.value;

        meta.loaders.buying.set(true);

        const [key, event] = Array.isArray(param)
            ? ['ids', 'bingo:buyPickedCards']
            : ['num_cards', 'bingo:buyCards'];

        const ticketQuantity = Array.isArray(param) ? param.length : param;
        const freePlayQuantity = useTokensFirst
            ? Math.min(ticketQuantity, account.player_fp_balance.value || 0)
            : 0;

        socket.emit(event, {
            ...injectStateInfo({
                game_id,
                game_number,
                [key]: param,
                sid,
            }),
            ...(freePlayQuantity > 0 ? { fp_qty: freePlayQuantity } : {}),
        });

        const callback = () => {
            if (!active()) return;
            meta.loaders.buying.set(false);
            pickedCards.selected.set([]);
            socket.off('bingo:buyCards', callback);
        };

        socket.on('bingo:buyCards', callback);
    };

    const autoBuy = () => {
        const {
            anyPrice,
            fixedPrice,
            games,
            maxTicketPrice,
            maxLoss,
            enableMaxLoss,
            enableUnlimited,
            enableMaxCards,
            tokensFirst,
            balance,
            tickets,
        } = autoBuyState.value;

        const ticketCost = getTicketCost(next);

        const $buyTicket = () => {
            if (next.fixed_game.value && !fixedPrice) {
                return;
            }

            const ticketsToBuy =
                next.fixed_game.value || enableMaxCards
                    ? getMaxCardsCanBuy(next)
                    : Math.min(getMaxCardsCanBuy(next), tickets);

            buyTicket(ticketsToBuy, tokensFirst);
        };

        const checkMaxLoss = () => {
            const accountBalance = getBalance();

            if (enableMaxLoss) {
                if (accountBalance >= balance - maxLoss) {
                    $buyTicket();
                } else {
                    openModal({
                        key: 'msgCommon',
                        name: 'autoBuy.maxLossThreshold',
                        closeBehavior: 'close',
                        autoClose: true,
                    });
                    autoBuyState.activation.set(false);
                }
            } else {
                $buyTicket();
            }
        };

        if (enableUnlimited && (anyPrice || ticketCost <= maxTicketPrice)) {
            checkMaxLoss();
            return;
        }

        if (games > 0 && (anyPrice || ticketCost <= maxTicketPrice)) {
            checkMaxLoss();
        }

        autoBuyState.games.set((games) => games - 1);

        if (games < 1) {
            openModal({
                key: 'msgCommon',
                name: 'autoBuy.gamesPlayedDone',
                closeBehavior: 'close',
                autoClose: true,
            });
            resetAutoBuy();
        }
    };

    const attemptAutoBuy = () => {
        if (!active()) return;
        if (isFreeBuyConfirm(next)) {
            meta.modals.freeCards.set(true);
            buyTicket(next.game_cards_max.value);
        } else if (autoBuyState.activation.value) {
            autoBuy();
        }
    };

    const getAccountStatus = () => {
        if (!active()) return Promise.resolve();
        const { game_sid: sid, game_number } = next.value;

        meta.loaders.accountStatus.set(true);

        return new Promise<void>((resolve) => {
            const cancel = () => {
                socket.off('bingo:accountStatus', callback);
                cancellations.delete(cancel);
                resolve();
            };
            const callback = () => {
                if (active()) meta.loaders.accountStatus.set(false);
                cancel();
            };
            cancellations.add(cancel);
            socket.on('bingo:accountStatus', callback);
            socket.emit(
                'bingo:getAccountStatus',
                injectStateInfo({
                    game_id,
                    game_number,
                    sid,
                })
            );
        });
    };

    const rejoin = () => {
        if (!active()) return;
        const currentGameSession = current.game_sid.value;
        const nextGameSession = next.game_sid.value;

        if (currentGameSession) {
            socket.emit('rejoin', currentGameSession);
        }

        if (nextGameSession) {
            socket.emit('rejoin', nextGameSession);
        }
    };

    type LoadRequest = {
        nextGame: boolean;
        resolve: (success: boolean) => void;
        // Handles the response in place of `initialize`, for partial updates.
        apply?: (response: BingoLoadGameResponse) => void;
    };
    const queue: LoadRequest[] = [];
    let cancelLoad: (() => void) | undefined;
    const runLoad = () => {
        if (cancelLoad || !active()) return;
        const request = queue.shift();
        if (!request) return;
        if (!request.nextGame) beginCurrentLoad();
        const finish = (success: boolean) => {
            clearTimeout(warning);
            clearTimeout(timeout);
            if (
                meta.modals.msgCommon.isOpen.value &&
                meta.modals.msgCommon.name.value === SLOW_RESPONSE_WARNING
            )
                closeModal('msgCommon');
            socket.off('bingo:loadGameResp', callback);
            cancelLoad = undefined;
            request.resolve(success);
        };
        const callback = (response: BingoLoadGameResponse) => {
            if (!active()) return;
            if (response.success && !!response.nextGame !== request.nextGame) {
                suspendApp({
                    event: 'bingo:loadGameResp',
                    expectedSessionId: current.game_sid.value,
                    receivedSessionId: response.data.game_sid,
                });
                return;
            }
            if (request.apply) request.apply(response);
            else initialize(response, attemptAutoBuy);
            if (!active()) return;
            if (!request.nextGame && !request.apply) {
                if (response.success) finishCurrentLoad();
                else cancelCurrentLoad();
            }
            if (!active()) return;
            finish(response.success);
            runLoad();
        };
        // Warn without suspending: slow connections may still respond.
        const warning = setTimeout(() => {
            if (!active() || meta.modals.msgCommon.isOpen.value) return;
            openModal({ key: 'msgCommon', name: SLOW_RESPONSE_WARNING });
        }, LOAD_GAME_WARNING_DELAY);
        const timeout = setTimeout(() => {
            suspendApp({
                event: 'bingo:loadGameResp:timeout',
                expectedSessionId: current.game_sid.value,
                receivedSessionId: null,
            });
        }, LOAD_GAME_TIMEOUT);
        cancelLoad = () => finish(false);
        socket.on('bingo:loadGameResp', callback);
        socket.emit(
            'bingo:loadGame',
            injectStateInfo({
                game_id,
                next_game: request.nextGame ? '1' : '0',
            })
        );
    };
    const loadGame = (nextGame = false): Promise<boolean> => {
        if (!active()) return Promise.resolve(false);
        // Defer session-bound events while a current load waits in the queue,
        // otherwise they'd be validated against the stale current game.
        if (!nextGame) beginCurrentLoad();
        return new Promise((resolve) => {
            queue.push({ nextGame, resolve });
            runLoad();
        });
    };
    // Refreshes only the next game's jackpot, e.g. after a jackpot win
    // resets the pool, without re-initializing the rest of the next game state.
    const refreshNextJackpot = (): Promise<boolean> => {
        if (!active()) return Promise.resolve(false);
        return new Promise((resolve) => {
            queue.push({
                nextGame: true,
                resolve,
                apply: (response) => {
                    if (
                        !response.success ||
                        response.data.game_sid !== next.game_sid.value
                    )
                        return;
                    next.jackpot.set(response.data.jackpot);
                },
            });
            runLoad();
        });
    };
    const cancelPending = () => {
        cancelLoad?.();
        for (const request of queue.splice(0)) request.resolve(false);
        for (const cancel of cancellations) cancel();
        cancelCurrentLoad();
    };
    socket.on('disconnect', cancelPending);
    const removeSuspensionListener = onSuspend(cancelPending);
    const dispose = () => {
        disposed = true;
        cancelPending();
        removeSuspensionListener();
        socket.off('disconnect', cancelPending);
    };

    const getPickedCards = () => {
        if (!active()) return;
        meta.loaders.pickedCards.set(true);

        socket.emit(
            'bingo:getPickedCards',
            injectStateInfo({
                game_id,
                game_number: next.game_number.value,
                sid: next.game_sid.value,
                isNext: pickedCards.hasNext.value,
            })
        );

        socket.on('bingo:getPickedCards', function callback() {
            if (!active()) return;
            meta.loaders.pickedCards.set(false);
            socket.off('bingo:getPickedCards', callback);
        });
    };

    return {
        dispose,
        buyTicket,
        attemptAutoBuy,
        getAccountStatus,
        loadGame,
        refreshNextJackpot,
        rejoin,
        injectStateInfo,
        getPickedCards,
    };
};
